import React, { Component } from "react";
import { List, Map, Set } from "immutable";
import PropTypes from "prop-types";
import ImmutablePropTypes from "react-immutable-proptypes";
import Airline from "./Airline";
import Flight from "./Flight";
import CabinClass from "./CabinClass";
import Cost from "./Cost";
import OfferGroupHeading from "./OfferGroupHeading";
import classnames from "classnames";
import scrollToComponent from "react-scroll-to-component";

/** Renders a single OfferGroup - for a FlightRequirement - (potentially within a number of such groups).
 * Independently manages the filters for, and selection within, this offergroup,
 * but with filters showing/hiding controlled externally, in order to show/hide
 * filters across all offer groups with one UI control.
 */
class FlightOfferGroup extends Component {
  constructor() {
    super();
    this.state = {
      showUnselected: false
    };
  }

  toggleShowUnselected() {
    this.setState({ showUnselected: !this.state.showUnselected });
  }

  selectOffer(uri, ref) {
    this.props.selectOffer(uri);
    this.setState({ showUnselected: false }, () => {
      scrollToComponent(ref, { offset: -96, align: "top", duration: 500, ease: "inQuad" });
    });
  }

  unselectOffer(uri, ref) {
    this.props.unselectOffer(uri);
    this.setState({ showUnselected: true }, () => {
      scrollToComponent(ref, { offset: -46, align: "top", duration: 500, ease: "inQuad" });
    });
  }

  render() {
    const {
      offerGroup = Map(),
      groupNo = 0,
      filterFn,
      passengerCount,
      airportByIata = Map(),
      showHeading = false,
      selectedOffers = Set()
    } = this.props;

    const { showUnselected } = this.state;

    const hasSelection = offerGroup.get("offer", List()).some(o => selectedOffers.contains(o.get("uri")));

    // Logic to decide if a given offer is shown or hidden
    // (per-offer flag is set)

    // FIXME we are setting the requested services in the offer. this is used for TimeOfDay filtering.
    const offers = offerGroup
      .get("offer", List())
      .map(o =>
        o.set(
          "match",
          selectedOffers.contains(o.get("uri")) ||
            ((filterFn ? filterFn(o.set("requested", offerGroup.get("requested"))) : true) && (!hasSelection || showUnselected))
        )
      );

    this.ref = {};
    return (
      <div className="offer-group">
        <OfferGroupHeading showHeading={showHeading} offerGroup={offerGroup} index={groupNo} view="book-a-flight">
          {/* If we have a selection, and multiple options to select from */}
          {hasSelection && offers.size > 1 && (
            <div className="show-hide-controls" style={{ float: "right" }}>
              {!showUnselected && (
                <button className="btn btn-sm btn-secondary show-unselected" onClick={this.toggleShowUnselected.bind(this)}>
                  {filterFn ? `Show all (that match filters)` : `Show all ${offers.size}`}
                </button>
              )}
              {showUnselected && (
                <button className="btn btn-sm btn-secondary hide-unselected" onClick={this.toggleShowUnselected.bind(this)}>
                  Hide unselected
                </button>
              )}
            </div>
          )}
        </OfferGroupHeading>

        {offers.map((o = Map()) => {
          const offerSelected = selectedOffers.includes(o.get("uri"));
          return (
            <div
              ref={div => {
                this.ref[o.get("uri")] = div;
              }}
              style={{
                display: o.get("match") ? "block" : "none",
                cursor: "pointer"
              }}
              className={classnames({
                "result-group": true,
                hoverable: true,
                selected: offerSelected
              })}
              key={o.get("uri")}
              onClick={() =>
                offerSelected
                  ? this.unselectOffer.bind(this)(o.get("uri"), this.ref[o.get("uri")])
                  : this.selectOffer.bind(this)(o.get("uri"), this.ref[o.get("uri")])
              }
            >
              {o.get("serviceDetail").map((sd = Map()) => (
                <div className="row result-item" key={sd.get("uri")}>
                  <div className="col-sm-3 col-lg-4 airline-col">
                    <Airline airline={sd.get("serviceProvider")} />
                  </div>
                  <Flight serviceDetail={sd} airportByIata={airportByIata} />
                  <div className="col-sm-4 col-lg-3 class-col">
                    <CabinClass serviceDetail={sd} />
                  </div>
                </div>
              ))}
              <div className="col-sm-5 col-lg-4 price-col">
                <Cost cost={o.get("cost")} passengerCount={passengerCount} selected={offerSelected}></Cost>
              </div>
              <i className={offerSelected ? "tl-corner-selected" : "tl-corner"} />
              <div
                className="col-xs-4 col-sm-2"
                style={{
                  textAlign: "center",
                  height: "20px",
                  fontSize: "0.7em",
                  position: "absolute",
                  bottom: "0",
                  paddingTop: "5px",
                  backgroundColor: o.get("sourceContentProviderName") === "Sabre" ? "#e50000" : "#2e5c99",
                  color: "white",
                  borderBottomLeftRadius: "4px",
                  borderTopRightRadius: "2em"
                }}
              >
                <div className="v-align" style={{ height: "100%", width: "100%", paddingLeft: "5px", paddingRight: "5px" }}>
                  {o.get("sourceContentProviderName")}
                </div>
              </div>
            </div>
          );
        })}
        {(offers.size === 0 || offers.every((o = Map()) => !o.get("match"))) && (
          <div className="no-results-placeholder" key="none">
            No results match your criteria
          </div>
        )}
      </div>
    );
  }
}

FlightOfferGroup.propTypes = {
  offerGroup: ImmutablePropTypes.map.isRequired,
  // Map<IataCode, AirportDetail>
  airportByIata: ImmutablePropTypes.map,
  // The number of passengers that these offers are for
  // (hack, but this is not in the offerGroups part of the data structure)
  // Instead, probably pass through 'requested' info
  passengerCount: PropTypes.number.isRequired,

  // Optional filter (predicate function)
  // offerFilter :: Offer -> Bool
  filterFn: PropTypes.func,

  // Add a given offer URI to the user selection
  // selectOffer :: String -> ()
  selectOffer: PropTypes.func,

  // Remove a given offer URI from the user selection
  // unselectOffer :: String -> ()
  unselectOffer: PropTypes.func,

  // Set<OfferURI> - the current user selection
  selectedOffers: ImmutablePropTypes.setOf(PropTypes.string)
};

/* Enhances OfferGroup with a stateful 'showUnselected' property
   and a toggle function, as well as filtering and selecting logic. */
// const enhance = withStateHandlers(
//     // Initial state
//     {
//         showUnselected: false
//     },
//     // Handlers
//     {
//         toggleShowUnselected: ({ showUnselected }) => () => ({
//             showUnselected: !showUnselected
//         }),
//
//         // Override behaviour of 'select' and 'unselect' accordingly
//         selectOffer: (_, { selectOffer }) => (uri,ref) => {
//             selectOffer(uri)
//             return { showUnselected: false, selectedRef: ref }
//         },
//         unselectOffer: (_, { unselectOffer }) => (uri,ref) => {
//             unselectOffer(uri)
//             return { showUnselected: true, selectedRef: ref }
//         }
//     }
// )

// export default enhance(FlightOfferGroup)
export default FlightOfferGroup;



// WEBPACK FOOTER //
// ./src/ndc/offer/FlightOfferGroup.js